// src/modulos/TUV/TUVChecklistModelPage.js

import React, { useState, useEffect} from 'react';
import {collection, addDoc, getDocs, deleteDoc, doc, updateDoc, query, orderBy } from 'firebase/firestore';
import { auth as firebaseAuth, db as firebaseDb } from '../../firebaseConfig';
import { FiEdit, FiTrash2 } from 'react-icons/fi';
import { RiFileTransferLine } from "react-icons/ri";
import { HiOutlineDuplicate } from "react-icons/hi";
import usePagePermissions from '../../hooks/usePagePermissions';
import styles from './TUVChecklistModelPage.module.css';

// --------------------------------------------------------------
// Firebase initialization
// --------------------------------------------------------------
const db = firebaseDb;
const auth = firebaseAuth;

// --------------------------------------------------------------
// Field Types Configuration
// --------------------------------------------------------------
const FIELD_TYPES = {
    AVALIATIVE: 'avaliative',
    TEXT: 'text',
    DATE: 'date',
    REGISTRATION: 'registration',
    NUMERIC: 'numeric',
    SELECTION: 'selection',
    SPECIAL: 'special'
};

const FIELD_TYPE_LABELS = {
    [FIELD_TYPES.AVALIATIVE]: 'Avaliativo',
    [FIELD_TYPES.TEXT]: 'Texto',
    [FIELD_TYPES.DATE]: 'Data',
    [FIELD_TYPES.REGISTRATION]: 'Cadastro',
    [FIELD_TYPES.NUMERIC]: 'Numérico',
    [FIELD_TYPES.SELECTION]: 'Lista de Seleção',
    [FIELD_TYPES.SPECIAL]: 'Especial'
};

// Sub-types for each field type
const SUB_TYPES = {
    TEXT: {
        SHORT: 'short',
        LONG: 'long'
    },
    DATE: {
        TIME_24H: 'time_24h',
        TIME_24H_S: 'time_24h_s',
        TIME_12H: 'time_12h',
        TIME_12H_S: 'time_12h_s',
        DATE_DDMMYYYY: 'date_ddmmyyyy',
        DATE_MMYYYY: 'date_mmyyyy'
    },
    REGISTRATION: {
        EMAIL: 'email',
        STATE: 'state',
        UF_CITY: 'uf_city',
        CEP: 'cep',
        PHONE: 'phone',
        CNPJ: 'cnpj',
        CPF: 'cpf',
        PLATE: 'plate',
        PLATE_MERCOSUL: 'plate_mercosul'
    },
    NUMERIC: {
        MONETARY: 'monetary',
        QUANTITY: 'quantity',
        DECIMAL: 'decimal',
        PERCENTAGE: 'percentage'
    },
    SELECTION: {
        SINGLE: 'single',
        MULTIPLE: 'multiple'
    },
    SPECIAL: {
        GPS_MANUAL: 'gps_manual',
        GPS_AUTOMATIC: 'gps_automatic'
    }
};

const SUB_TYPE_LABELS = {
    [SUB_TYPES.TEXT.SHORT]: 'Texto Curto',
    [SUB_TYPES.TEXT.LONG]: 'Texto Longo',
    [SUB_TYPES.DATE.TIME_24H]: 'Horário 24h (00:00)',
    [SUB_TYPES.DATE.TIME_24H_S]: 'Horário 24h (00:00:00)',
    [SUB_TYPES.DATE.TIME_12H]: 'Horário 12h (00:00 AM/PM)',
    [SUB_TYPES.DATE.TIME_12H_S]: 'Horário 12h (00:00:00 AM/PM)',
    [SUB_TYPES.DATE.DATE_DDMMYYYY]: 'Data (dd/mm/aaaa)',
    [SUB_TYPES.DATE.DATE_MMYYYY]: 'Data (mm/aaaa)',
    [SUB_TYPES.REGISTRATION.EMAIL]: 'Email',
    [SUB_TYPES.REGISTRATION.STATE]: 'Estado',
    [SUB_TYPES.REGISTRATION.UF_CITY]: 'UF e Cidade',
    [SUB_TYPES.REGISTRATION.CEP]: 'CEP',
    [SUB_TYPES.REGISTRATION.PHONE]: 'Telefone',
    [SUB_TYPES.REGISTRATION.CNPJ]: 'CNPJ',
    [SUB_TYPES.REGISTRATION.CPF]: 'CPF',
    [SUB_TYPES.REGISTRATION.PLATE]: 'Placa Automóvel',
    [SUB_TYPES.REGISTRATION.PLATE_MERCOSUL]: 'Placa Mercosul',
    [SUB_TYPES.NUMERIC.MONETARY]: 'Monetário',
    [SUB_TYPES.NUMERIC.QUANTITY]: 'Quantidade',
    [SUB_TYPES.NUMERIC.DECIMAL]: 'Decimal',
    [SUB_TYPES.NUMERIC.PERCENTAGE]: 'Porcentagem',
    [SUB_TYPES.SELECTION.SINGLE]: 'Seleção Única',
    [SUB_TYPES.SELECTION.MULTIPLE]: 'Seleção Múltipla',
    [SUB_TYPES.SPECIAL.GPS_MANUAL]: 'GPS Manual',
    [SUB_TYPES.SPECIAL.GPS_AUTOMATIC]: 'GPS Automático'
};

// --------------------------------------------------------------
// GPS Types Configuration
// --------------------------------------------------------------
const GPS_TYPES = {
    MANUAL: 'manual',
    AUTOMATIC: 'automatic'
};

const GPS_TYPE_LABELS = {
    [GPS_TYPES.MANUAL]: 'Manual (digitar coordenadas)',
    [GPS_TYPES.AUTOMATIC]: 'Automático (capturar localização)'
};

// Complement types
const COMPLEMENT_TYPES = {
    COMMENTARY: 'commentary',
    ACTION_PLAN: 'action_plan',
    ATTACHMENT: 'attachment',
    SIGNATURE: 'signature',
    NA: 'na'
};

const COMPLEMENT_LABELS = {
    [COMPLEMENT_TYPES.COMMENTARY]: 'Comentário',
    [COMPLEMENT_TYPES.ACTION_PLAN]: 'Plano de Ação',
    [COMPLEMENT_TYPES.ATTACHMENT]: 'Anexo',
    [COMPLEMENT_TYPES.SIGNATURE]: 'Assinatura',
    [COMPLEMENT_TYPES.NA]: 'N/A (Não se Aplica)'
};

// Currencies
const CURRENCIES = [
    { code: 'BRL', symbol: 'R$', label: 'Real (R$)' },
    { code: 'USD', symbol: 'US$', label: 'Dólar (US$)' },
    { code: 'EUR', symbol: '€', label: 'Euro (€)' },
    { code: 'GBP', symbol: '£', label: 'Libra (£)' },
    { code: 'ARS', symbol: 'AR$', label: 'Peso Argentino (AR$)' },
    { code: 'UYU', symbol: '$U', label: 'Peso Uruguayo ($U)' },
    { code: 'PYG', symbol: '₲', label: 'Guaraní (₲)' },
    { code: 'CLP', symbol: 'CL$', label: 'Peso Chileno (CL$)' }
];

// --------------------------------------------------------------
// Main Component
// --------------------------------------------------------------
const TUVChecklistModelPage = () => {
    // --- Permission Hook ---
    const can = usePagePermissions('tuv_modelo_checklist');

    const [currentUser, setCurrentUser] = useState(null);
    const [checklists, setChecklists] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingChecklist, setEditingChecklist] = useState(null);
    const [status, setStatus] = useState({ message: '', type: '' });

    // Form state for new/edit checklist
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        category: '',
        areas: [],
        isActive: true,
        version: '1.0'
    });

    // Area editor state
    const [editingAreaIndex, setEditingAreaIndex] = useState(null);
    const [areaEditorOpen, setAreaEditorOpen] = useState(false);
    const [areaForm, setAreaForm] = useState({
        title: '',
        description: '',
        items: [],
        subAreas: []
    });

    // Item editor state
    const [editingItemContext, setEditingItemContext] = useState(null);
    const [itemEditorOpen, setItemEditorOpen] = useState(false);
    const [itemForm, setItemForm] = useState({
        title: '',
        type: FIELD_TYPES.TEXT,
        subType: SUB_TYPES.TEXT.SHORT,
        required: false,
        options: [],
        currency: 'BRL',
        gpsType: GPS_TYPES.MANUAL,
        complements: {
            commentary: { enabled: false, required: false },
            action_plan: { enabled: false, required: false },
            attachment: { enabled: false, required: false },
            signature: { enabled: false, required: false },
            na: { enabled: false, required: false }
        }
    });

    // Sub-area editor state
    const [subAreaEditorOpen, setSubAreaEditorOpen] = useState(false);
    const [subAreaForm, setSubAreaForm] = useState({
        title: '',
        description: '',
        items: []
    });
    const [editingSubAreaContext, setEditingSubAreaContext] = useState(null);

    // Preview mode
    const [previewMode, setPreviewMode] = useState(false);

    // --------------------------------------------------------------
    // Auth & Data Loading
    // --------------------------------------------------------------
    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged((user) => {
            setCurrentUser(user);
            if (user) {
                loadChecklists();
            } else {
                setChecklists([]);
                setLoading(false);
            }
        });
        return () => unsubscribe();
    }, []);

    const loadChecklists = async () => {
        setLoading(true);
        try {
            const q = query(
                collection(db, 'tuv_checklist_models'),
                orderBy('createdAt', 'desc')
            );
            const querySnapshot = await getDocs(q);
            const loaded = querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setChecklists(loaded);
        } catch (error) {
            console.error('Error loading checklists:', error);
            setStatus({ message: 'Erro ao carregar checklists', type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    // --------------------------------------------------------------
    // Area Management
    // --------------------------------------------------------------
    const addArea = () => {
        setAreaForm({ title: '', description: '', items: [], subAreas: [] });
        setEditingAreaIndex(null);
        setAreaEditorOpen(true);
    };

    const editArea = (index) => {
        const area = formData.areas[index];
        setAreaForm({ ...area });
        setEditingAreaIndex(index);
        setAreaEditorOpen(true);
    };

    const saveArea = () => {
        if (!areaForm.title.trim()) {
            setStatus({ message: 'Título da área é obrigatório', type: 'error' });
            return;
        }

        const newArea = {
            ...areaForm,
            title: areaForm.title.trim(),
            description: areaForm.description.trim()
        };

        if (editingAreaIndex !== null) {
            const updatedAreas = [...formData.areas];
            updatedAreas[editingAreaIndex] = newArea;
            setFormData(prev => ({ ...prev, areas: updatedAreas }));
        } else {
            setFormData(prev => ({
                ...prev,
                areas: [...prev.areas, newArea]
            }));
        }

        setAreaEditorOpen(false);
        setEditingAreaIndex(null);
        setStatus({ message: 'Área salva com sucesso!', type: 'success' });
        setTimeout(() => setStatus({ message: '', type: '' }), 3000);
    };

    const deleteArea = (index) => {
        if (window.confirm('Tem certeza que deseja excluir esta área?')) {
            setFormData(prev => ({
                ...prev,
                areas: prev.areas.filter((_, i) => i !== index)
            }));
        }
    };

    const moveArea = (index, direction) => {
        const newAreas = [...formData.areas];
        const targetIndex = index + direction;
        if (targetIndex < 0 || targetIndex >= newAreas.length) return;
        [newAreas[index], newAreas[targetIndex]] = [newAreas[targetIndex], newAreas[index]];
        setFormData(prev => ({ ...prev, areas: newAreas }));
    };

    // --------------------------------------------------------------
    // Item Management (within Area or SubArea)
    // --------------------------------------------------------------
    const addItem = (context) => {
        setItemForm({
            title: '',
            type: FIELD_TYPES.TEXT,
            subType: SUB_TYPES.TEXT.SHORT,
            required: false,
            options: [],
            currency: 'BRL',
            gpsType: GPS_TYPES.MANUAL,
            complements: {
                commentary: { enabled: false, required: false },
                action_plan: { enabled: false, required: false },
                attachment: { enabled: false, required: false },
                signature: { enabled: false, required: false },
                na: { enabled: false, required: false }
            }
        });
        setEditingItemContext(context);
        setItemEditorOpen(true);
    };

    const editItem = (context, itemIndex) => {
        const item = getItemFromContext(context, itemIndex);
        setItemForm({ ...item });
        setEditingItemContext({ ...context, itemIndex });
        setItemEditorOpen(true);
    };

    const saveItem = () => {
        if (!itemForm.title.trim()) {
            setStatus({ message: 'Título do item é obrigatório', type: 'error' });
            return;
        }

        const newItem = {
            ...itemForm,
            title: itemForm.title.trim()
        };

        if (editingItemContext && editingItemContext.itemIndex !== undefined) {
            // Editing existing item
            const { areaIndex, subAreaIndex, itemIndex } = editingItemContext;
            if (subAreaIndex !== undefined) {
                // Item in sub-area
                const updatedAreas = [...formData.areas];
                const subArea = updatedAreas[areaIndex].subAreas[subAreaIndex];
                subArea.items[itemIndex] = newItem;
                setFormData(prev => ({ ...prev, areas: updatedAreas }));
            } else {
                // Item in main area
                const updatedAreas = [...formData.areas];
                updatedAreas[areaIndex].items[itemIndex] = newItem;
                setFormData(prev => ({ ...prev, areas: updatedAreas }));
            }
        } else {
            // Adding new item
            const { areaIndex, subAreaIndex } = editingItemContext;
            if (subAreaIndex !== undefined) {
                // Add to sub-area
                const updatedAreas = [...formData.areas];
                updatedAreas[areaIndex].subAreas[subAreaIndex].items.push(newItem);
                setFormData(prev => ({ ...prev, areas: updatedAreas }));
            } else {
                // Add to main area
                const updatedAreas = [...formData.areas];
                updatedAreas[areaIndex].items.push(newItem);
                setFormData(prev => ({ ...prev, areas: updatedAreas }));
            }
        }

        setItemEditorOpen(false);
        setEditingItemContext(null);
        setStatus({ message: 'Item salvo com sucesso!', type: 'success' });
        setTimeout(() => setStatus({ message: '', type: '' }), 3000);
    };

    const deleteItem = (context, itemIndex) => {
        if (window.confirm('Tem certeza que deseja excluir este item?')) {
            const { areaIndex, subAreaIndex } = context;
            if (subAreaIndex !== undefined) {
                const updatedAreas = [...formData.areas];
                updatedAreas[areaIndex].subAreas[subAreaIndex].items.splice(itemIndex, 1);
                setFormData(prev => ({ ...prev, areas: updatedAreas }));
            } else {
                const updatedAreas = [...formData.areas];
                updatedAreas[areaIndex].items.splice(itemIndex, 1);
                setFormData(prev => ({ ...prev, areas: updatedAreas }));
            }
        }
    };

    const moveItem = (context, itemIndex, direction) => {
        const { areaIndex, subAreaIndex } = context;
        let items;
        const updatedAreas = [...formData.areas];

        if (subAreaIndex !== undefined) {
            items = updatedAreas[areaIndex].subAreas[subAreaIndex].items;
        } else {
            items = updatedAreas[areaIndex].items;
        }

        const targetIndex = itemIndex + direction;
        if (targetIndex < 0 || targetIndex >= items.length) return;
        [items[itemIndex], items[targetIndex]] = [items[targetIndex], items[itemIndex]];
        setFormData(prev => ({ ...prev, areas: updatedAreas }));
    };

    const getItemFromContext = (context, itemIndex) => {
        const { areaIndex, subAreaIndex } = context;
        if (subAreaIndex !== undefined) {
            return formData.areas[areaIndex].subAreas[subAreaIndex].items[itemIndex];
        }
        return formData.areas[areaIndex].items[itemIndex];
    };

    // --------------------------------------------------------------
    // Sub-Area Management
    // --------------------------------------------------------------
    const addSubArea = (areaIndex) => {
        setSubAreaForm({ title: '', description: '', items: [] });
        setEditingSubAreaContext({ areaIndex, subAreaIndex: null });
        setSubAreaEditorOpen(true);
    };

    const editSubArea = (areaIndex, subAreaIndex) => {
        const subArea = formData.areas[areaIndex].subAreas[subAreaIndex];
        setSubAreaForm({ ...subArea });
        setEditingSubAreaContext({ areaIndex, subAreaIndex });
        setSubAreaEditorOpen(true);
    };

    const saveSubArea = () => {
        if (!subAreaForm.title.trim()) {
            setStatus({ message: 'Título da subárea é obrigatório', type: 'error' });
            return;
        }

        const newSubArea = {
            ...subAreaForm,
            title: subAreaForm.title.trim(),
            description: subAreaForm.description.trim()
        };

        const { areaIndex, subAreaIndex } = editingSubAreaContext;
        const updatedAreas = [...formData.areas];

        if (subAreaIndex !== null) {
            updatedAreas[areaIndex].subAreas[subAreaIndex] = newSubArea;
        } else {
            updatedAreas[areaIndex].subAreas.push(newSubArea);
        }

        setFormData(prev => ({ ...prev, areas: updatedAreas }));
        setSubAreaEditorOpen(false);
        setEditingSubAreaContext(null);
        setStatus({ message: 'Subárea salva com sucesso!', type: 'success' });
        setTimeout(() => setStatus({ message: '', type: '' }), 3000);
    };

    const deleteSubArea = (areaIndex, subAreaIndex) => {
        if (window.confirm('Tem certeza que deseja excluir esta subárea?')) {
            const updatedAreas = [...formData.areas];
            updatedAreas[areaIndex].subAreas.splice(subAreaIndex, 1);
            setFormData(prev => ({ ...prev, areas: updatedAreas }));
        }
    };

    const moveSubArea = (areaIndex, subAreaIndex, direction) => {
        const updatedAreas = [...formData.areas];
        const subAreas = updatedAreas[areaIndex].subAreas;
        const targetIndex = subAreaIndex + direction;
        if (targetIndex < 0 || targetIndex >= subAreas.length) return;
        [subAreas[subAreaIndex], subAreas[targetIndex]] = [subAreas[targetIndex], subAreas[subAreaIndex]];
        setFormData(prev => ({ ...prev, areas: updatedAreas }));
    };

    // --------------------------------------------------------------
    // CRUD Operations
    // --------------------------------------------------------------
    const saveChecklist = async () => {
        if (!currentUser) {
            setStatus({ message: 'Usuário não autenticado', type: 'error' });
            return;
        }

        if (!formData.name.trim()) {
            setStatus({ message: 'Nome do checklist é obrigatório', type: 'error' });
            return;
        }

        setLoading(true);
        try {
            const checklistData = {
                name: formData.name.trim(),
                description: formData.description.trim(),
                category: formData.category.trim(),
                areas: formData.areas,
                isActive: formData.isActive,
                version: formData.version,
                areaCount: formData.areas.length,
                createdBy: currentUser.displayName || currentUser.email || 'Usuário',
                createdByUid: currentUser.uid,
                updatedAt: new Date().toISOString()
            };

            if (editingChecklist) {
                await updateDoc(doc(db, 'tuv_checklist_models', editingChecklist.id), {
                    ...checklistData,
                    updatedAt: new Date().toISOString()
                });
                setStatus({ message: 'Checklist atualizado com sucesso!', type: 'success' });
            } else {
                await addDoc(collection(db, 'tuv_checklist_models'), {
                    ...checklistData,
                    createdAt: new Date().toISOString()
                });
                setStatus({ message: 'Checklist criado com sucesso!', type: 'success' });
            }

            await loadChecklists();
            closeModal();
        } catch (error) {
            console.error('Error saving checklist:', error);
            setStatus({ message: 'Erro ao salvar checklist', type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const deleteChecklist = async (checklist) => {
        if (!can.delete) {
            setStatus({ message: 'Você não tem permissão para excluir checklists.', type: 'error' });
            return;
        }

        if (!window.confirm(`Tem certeza que deseja excluir o checklist "${checklist.name}"?`)) return;

        try {
            await deleteDoc(doc(db, 'tuv_checklist_models', checklist.id));
            await loadChecklists();
            setStatus({ message: 'Checklist excluído com sucesso!', type: 'success' });
        } catch (error) {
            console.error('Error deleting checklist:', error);
            setStatus({ message: 'Erro ao excluir checklist', type: 'error' });
        }
        setTimeout(() => setStatus({ message: '', type: '' }), 3000);
    };

    const duplicateChecklist = async (checklist) => {
        if (!can.create) {
            setStatus({ message: 'Você não tem permissão para duplicar checklists.', type: 'error' });
            return;
        }

        try {
            const newChecklist = {
                name: `${checklist.name} (Cópia)`,
                description: checklist.description,
                category: checklist.category,
                areas: JSON.parse(JSON.stringify(checklist.areas)),
                isActive: checklist.isActive,
                version: checklist.version,
                areaCount: checklist.areaCount,
                createdBy: currentUser?.displayName || currentUser?.email || 'Usuário',
                createdByUid: currentUser?.uid,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString()
            };
            await addDoc(collection(db, 'tuv_checklist_models'), newChecklist);
            await loadChecklists();
            setStatus({ message: 'Checklist duplicado com sucesso!', type: 'success' });
        } catch (error) {
            console.error('Error duplicating checklist:', error);
            setStatus({ message: 'Erro ao duplicar checklist', type: 'error' });
        }
        setTimeout(() => setStatus({ message: '', type: '' }), 3000);
    };

    // --------------------------------------------------------------
    // Modal Controls
    // --------------------------------------------------------------
    const openModal = (checklist = null) => {
        if (checklist) {
            if (!can.edit) {
                setStatus({ message: 'Você não tem permissão para editar checklists.', type: 'error' });
                return;
            }
            setEditingChecklist(checklist);
            setFormData({
                name: checklist.name || '',
                description: checklist.description || '',
                category: checklist.category || '',
                areas: checklist.areas || [],
                isActive: checklist.isActive !== undefined ? checklist.isActive : true,
                version: checklist.version || '1.0'
            });
        } else {
            if (!can.create) {
                setStatus({ message: 'Você não tem permissão para criar checklists.', type: 'error' });
                return;
            }
            setEditingChecklist(null);
            setFormData({
                name: '',
                description: '',
                category: '',
                areas: [],
                isActive: true,
                version: '1.0'
            });
        }
        setPreviewMode(false);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setEditingChecklist(null);
        setAreaEditorOpen(false);
        setItemEditorOpen(false);
        setSubAreaEditorOpen(false);
        setPreviewMode(false);
    };

    // --------------------------------------------------------------
    // Export / Import
    // --------------------------------------------------------------
    const exportChecklist = (checklist) => {
        const data = {
            name: checklist.name,
            description: checklist.description,
            category: checklist.category,
            areas: checklist.areas,
            version: checklist.version,
            exportedAt: new Date().toISOString()
        };
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `checklist_${checklist.name.replace(/\s+/g, '_')}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    // --------------------------------------------------------------
    // Render Functions
    // --------------------------------------------------------------
    const renderStatusBadge = () => {
        if (!status.message) return null;
        return (
            <div className={`${styles.alert} ${styles[status.type]}`}>
                <span className={styles.alertIcon}>
                    {status.type === 'success' ? '✅' : status.type === 'error' ? '⚠️' : 'ℹ️'}
                </span>
                {status.message}
            </div>
        );
    };

    const renderComplementOptions = (complements, onChange, fieldType) => {
        // Filter complements based on field type
        const getAvailableComplements = () => {
            const allComplements = Object.entries(COMPLEMENT_LABELS);

            // If field type is AVALIATIVE, include N/A
            if (fieldType === FIELD_TYPES.AVALIATIVE) {
                return allComplements;
            }

            // Otherwise, exclude N/A
            return allComplements.filter(([key]) => key !== COMPLEMENT_TYPES.NA);
        };

        const availableComplements = getAvailableComplements();

        return (
            <div className={styles.complementSection}>
                <label className={styles.complementLabel}>Complementos</label>
                <div className={styles.complementGrid}>
                    {availableComplements.map(([key, label]) => (
                        <div key={key} className={styles.complementItem}>
                            <label className={styles.complementCheckbox}>
                                <input
                                    type="checkbox"
                                    checked={complements[key]?.enabled || false}
                                    onChange={(e) => {
                                        const newComplements = { ...complements };
                                        newComplements[key] = {
                                            ...newComplements[key],
                                            enabled: e.target.checked,
                                            required: e.target.checked ? (newComplements[key]?.required || false) : false
                                        };
                                        onChange(newComplements);
                                    }}
                                />
                                {label}
                            </label>
                            {complements[key]?.enabled && (
                                <label className={styles.complementRequired}>
                                    <input
                                        type="checkbox"
                                        checked={complements[key]?.required || false}
                                        onChange={(e) => {
                                            const newComplements = { ...complements };
                                            newComplements[key] = {
                                                ...newComplements[key],
                                                required: e.target.checked
                                            };
                                            onChange(newComplements);
                                        }}
                                    />
                                    Obrigatório
                                </label>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    const renderItemEditor = () => {
        if (!itemEditorOpen) return null;

        const getSubTypeOptions = () => {
            switch (itemForm.type) {
                case FIELD_TYPES.TEXT:
                    return [
                        { value: SUB_TYPES.TEXT.SHORT, label: SUB_TYPE_LABELS[SUB_TYPES.TEXT.SHORT] },
                        { value: SUB_TYPES.TEXT.LONG, label: SUB_TYPE_LABELS[SUB_TYPES.TEXT.LONG] }
                    ];
                case FIELD_TYPES.DATE:
                    return [
                        { value: SUB_TYPES.DATE.TIME_24H, label: SUB_TYPE_LABELS[SUB_TYPES.DATE.TIME_24H] },
                        { value: SUB_TYPES.DATE.TIME_24H_S, label: SUB_TYPE_LABELS[SUB_TYPES.DATE.TIME_24H_S] },
                        { value: SUB_TYPES.DATE.TIME_12H, label: SUB_TYPE_LABELS[SUB_TYPES.DATE.TIME_12H] },
                        { value: SUB_TYPES.DATE.TIME_12H_S, label: SUB_TYPE_LABELS[SUB_TYPES.DATE.TIME_12H_S] },
                        { value: SUB_TYPES.DATE.DATE_DDMMYYYY, label: SUB_TYPE_LABELS[SUB_TYPES.DATE.DATE_DDMMYYYY] },
                        { value: SUB_TYPES.DATE.DATE_MMYYYY, label: SUB_TYPE_LABELS[SUB_TYPES.DATE.DATE_MMYYYY] }
                    ];
                case FIELD_TYPES.REGISTRATION:
                    return [
                        { value: SUB_TYPES.REGISTRATION.EMAIL, label: SUB_TYPE_LABELS[SUB_TYPES.REGISTRATION.EMAIL] },
                        { value: SUB_TYPES.REGISTRATION.STATE, label: SUB_TYPE_LABELS[SUB_TYPES.REGISTRATION.STATE] },
                        { value: SUB_TYPES.REGISTRATION.UF_CITY, label: SUB_TYPE_LABELS[SUB_TYPES.REGISTRATION.UF_CITY] },
                        { value: SUB_TYPES.REGISTRATION.CEP, label: SUB_TYPE_LABELS[SUB_TYPES.REGISTRATION.CEP] },
                        { value: SUB_TYPES.REGISTRATION.PHONE, label: SUB_TYPE_LABELS[SUB_TYPES.REGISTRATION.PHONE] },
                        { value: SUB_TYPES.REGISTRATION.CNPJ, label: SUB_TYPE_LABELS[SUB_TYPES.REGISTRATION.CNPJ] },
                        { value: SUB_TYPES.REGISTRATION.CPF, label: SUB_TYPE_LABELS[SUB_TYPES.REGISTRATION.CPF] },
                        { value: SUB_TYPES.REGISTRATION.PLATE, label: SUB_TYPE_LABELS[SUB_TYPES.REGISTRATION.PLATE] },
                        { value: SUB_TYPES.REGISTRATION.PLATE_MERCOSUL, label: SUB_TYPE_LABELS[SUB_TYPES.REGISTRATION.PLATE_MERCOSUL] }
                    ];
                case FIELD_TYPES.NUMERIC:
                    return [
                        { value: SUB_TYPES.NUMERIC.MONETARY, label: SUB_TYPE_LABELS[SUB_TYPES.NUMERIC.MONETARY] },
                        { value: SUB_TYPES.NUMERIC.QUANTITY, label: SUB_TYPE_LABELS[SUB_TYPES.NUMERIC.QUANTITY] },
                        { value: SUB_TYPES.NUMERIC.DECIMAL, label: SUB_TYPE_LABELS[SUB_TYPES.NUMERIC.DECIMAL] },
                        { value: SUB_TYPES.NUMERIC.PERCENTAGE, label: SUB_TYPE_LABELS[SUB_TYPES.NUMERIC.PERCENTAGE] }
                    ];
                case FIELD_TYPES.SELECTION:
                    return [
                        { value: SUB_TYPES.SELECTION.SINGLE, label: SUB_TYPE_LABELS[SUB_TYPES.SELECTION.SINGLE] },
                        { value: SUB_TYPES.SELECTION.MULTIPLE, label: SUB_TYPE_LABELS[SUB_TYPES.SELECTION.MULTIPLE] }
                    ];
                case FIELD_TYPES.SPECIAL:
                    return [
                        { value: SUB_TYPES.SPECIAL.GPS_MANUAL, label: SUB_TYPE_LABELS[SUB_TYPES.SPECIAL.GPS_MANUAL] },
                        { value: SUB_TYPES.SPECIAL.GPS_AUTOMATIC, label: SUB_TYPE_LABELS[SUB_TYPES.SPECIAL.GPS_AUTOMATIC] }
                    ];
                default:
                    return [];
            }
        };

        const showCurrency = itemForm.type === FIELD_TYPES.NUMERIC && itemForm.subType === SUB_TYPES.NUMERIC.MONETARY;
        const showOptions = itemForm.type === FIELD_TYPES.SELECTION;
        const showGPSOptions = itemForm.type === FIELD_TYPES.SPECIAL &&
            (itemForm.subType === SUB_TYPES.SPECIAL.GPS_MANUAL || itemForm.subType === SUB_TYPES.SPECIAL.GPS_AUTOMATIC);

        return (
            <div className={styles.fieldEditorOverlay}>
                <div className={styles.fieldEditorModal}>
                    <div className={styles.fieldEditorHeader}>
                        <h3>{editingItemContext?.itemIndex !== undefined ? 'Editar Item' : 'Novo Item'}</h3>
                        <button
                            type="button"
                            className={styles.fieldEditorClose}
                            onClick={() => {
                                setItemEditorOpen(false);
                                setEditingItemContext(null);
                            }}
                        >
                            ×
                        </button>
                    </div>

                    <div className={styles.fieldEditorBody}>
                        <div className={styles.fieldEditorRow}>
                            <div className={styles.fieldEditorGroup}>
                                <label>
                                    Título do Item <span className={styles.required}>*</span>
                                </label>
                                <input
                                    type="text"
                                    value={itemForm.title}
                                    onChange={(e) => setItemForm({ ...itemForm, title: e.target.value })}
                                    placeholder="Ex: Nome do Cliente"
                                />
                            </div>
                            <div className={styles.fieldEditorGroup}>
                                <label>Tipo</label>
                                <select
                                    value={itemForm.type}
                                    onChange={(e) => {
                                        const newType = e.target.value;
                                        let subType = '';
                                        switch (newType) {
                                            case FIELD_TYPES.TEXT:
                                                subType = SUB_TYPES.TEXT.SHORT;
                                                break;
                                            case FIELD_TYPES.DATE:
                                                subType = SUB_TYPES.DATE.TIME_24H;
                                                break;
                                            case FIELD_TYPES.REGISTRATION:
                                                subType = SUB_TYPES.REGISTRATION.EMAIL;
                                                break;
                                            case FIELD_TYPES.NUMERIC:
                                                subType = SUB_TYPES.NUMERIC.QUANTITY;
                                                break;
                                            case FIELD_TYPES.SELECTION:
                                                subType = SUB_TYPES.SELECTION.SINGLE;
                                                break;
                                            case FIELD_TYPES.SPECIAL:
                                                subType = SUB_TYPES.SPECIAL.GPS_MANUAL;
                                                break;
                                            default:
                                                subType = '';
                                        }
                                        setItemForm({
                                            ...itemForm,
                                            type: newType,
                                            subType,
                                            options: newType === FIELD_TYPES.SELECTION ? [] : itemForm.options
                                        });
                                    }}
                                >
                                    {Object.entries(FIELD_TYPE_LABELS).map(([key, label]) => (
                                        <option key={key} value={key}>{label}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className={styles.fieldEditorRow}>
                            <div className={styles.fieldEditorGroup}>
                                <label>Subtipo</label>
                                <select
                                    value={itemForm.subType}
                                    onChange={(e) => setItemForm({ ...itemForm, subType: e.target.value })}
                                >
                                    {getSubTypeOptions().map(opt => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                </select>
                            </div>
                            <div className={styles.fieldEditorGroup}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <input
                                        type="checkbox"
                                        checked={itemForm.required}
                                        onChange={(e) => setItemForm({ ...itemForm, required: e.target.checked })}
                                    />
                                    Campo Obrigatório
                                </label>
                            </div>
                        </div>

                        {showCurrency && (
                            <div className={styles.fieldEditorGroup}>
                                <label>Moeda</label>
                                <select
                                    value={itemForm.currency}
                                    onChange={(e) => setItemForm({ ...itemForm, currency: e.target.value })}
                                >
                                    {CURRENCIES.map(curr => (
                                        <option key={curr.code} value={curr.code}>
                                            {curr.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}

                        {showGPSOptions && (
                            <div className={styles.fieldEditorGroup}>
                                <label>Tipo de GPS</label>
                                <select
                                    value={itemForm.gpsType || GPS_TYPES.MANUAL}
                                    onChange={(e) => setItemForm({ ...itemForm, gpsType: e.target.value })}
                                >
                                    {Object.entries(GPS_TYPE_LABELS).map(([key, label]) => (
                                        <option key={key} value={key}>{label}</option>
                                    ))}
                                </select>
                            </div>
                        )}

                        {showOptions && (
                            <div className={styles.fieldEditorGroup}>
                                <label>Opções</label>
                                <div className={styles.optionsContainer}>
                                    {itemForm.options.map((opt, index) => (
                                        <div key={index} className={styles.optionItem}>
                                            <input
                                                type="text"
                                                value={opt.label || ''}
                                                onChange={(e) => {
                                                    const newOptions = [...itemForm.options];
                                                    newOptions[index] = { ...newOptions[index], label: e.target.value };
                                                    setItemForm({ ...itemForm, options: newOptions });
                                                }}
                                                placeholder="Opção"
                                            />
                                            <button
                                                type="button"
                                                className={styles.optionRemove}
                                                onClick={() => {
                                                    const newOptions = [...itemForm.options];
                                                    newOptions.splice(index, 1);
                                                    setItemForm({ ...itemForm, options: newOptions });
                                                }}
                                            >
                                                ×
                                            </button>
                                        </div>
                                    ))}
                                    <button
                                        type="button"
                                        className={styles.optionAdd}
                                        onClick={() => {
                                            setItemForm({
                                                ...itemForm,
                                                options: [...itemForm.options, { label: '', complements: {} }]
                                            });
                                        }}
                                    >
                                        + Adicionar Opção
                                    </button>
                                </div>
                            </div>
                        )}

                        {renderComplementOptions(itemForm.complements, (newComplements) => {
                            setItemForm({ ...itemForm, complements: newComplements });
                        }, itemForm.type)}
                    </div>

                    <div className={styles.fieldEditorFooter}>
                        <button
                            type="button"
                            className={styles.fieldEditorCancel}
                            onClick={() => {
                                setItemEditorOpen(false);
                                setEditingItemContext(null);
                            }}
                        >
                            Cancelar
                        </button>
                        <button
                            type="button"
                            className={styles.fieldEditorSave}
                            onClick={saveItem}
                        >
                            {editingItemContext?.itemIndex !== undefined ? 'Atualizar' : 'Adicionar'}
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    const renderSubAreaEditor = () => {
        if (!subAreaEditorOpen) return null;

        return (
            <div className={styles.fieldEditorOverlay}>
                <div className={styles.fieldEditorModal}>
                    <div className={styles.fieldEditorHeader}>
                        <h3>{editingSubAreaContext?.subAreaIndex !== null ? 'Editar Subárea' : 'Nova Subárea'}</h3>
                        <button
                            type="button"
                            className={styles.fieldEditorClose}
                            onClick={() => {
                                setSubAreaEditorOpen(false);
                                setEditingSubAreaContext(null);
                            }}
                        >
                            ×
                        </button>
                    </div>

                    <div className={styles.fieldEditorBody}>
                        <div className={styles.fieldEditorGroup}>
                            <label>
                                Título da Subárea <span className={styles.required}>*</span>
                            </label>
                            <input
                                type="text"
                                value={subAreaForm.title}
                                onChange={(e) => setSubAreaForm({ ...subAreaForm, title: e.target.value })}
                                placeholder="Ex: Dados Técnicos"
                            />
                        </div>
                        <div className={styles.fieldEditorGroup}>
                            <label>Descrição</label>
                            <textarea
                                value={subAreaForm.description}
                                onChange={(e) => setSubAreaForm({ ...subAreaForm, description: e.target.value })}
                                placeholder="Descreva o propósito desta subárea"
                                rows={3}
                            />
                        </div>
                    </div>

                    <div className={styles.fieldEditorFooter}>
                        <button
                            type="button"
                            className={styles.fieldEditorCancel}
                            onClick={() => {
                                setSubAreaEditorOpen(false);
                                setEditingSubAreaContext(null);
                            }}
                        >
                            Cancelar
                        </button>
                        <button
                            type="button"
                            className={styles.fieldEditorSave}
                            onClick={saveSubArea}
                        >
                            {editingSubAreaContext?.subAreaIndex !== null ? 'Atualizar' : 'Adicionar'}
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    const renderAreaEditor = () => {
        if (!areaEditorOpen) return null;

        return (
            <div className={styles.fieldEditorOverlay}>
                <div className={styles.fieldEditorModal}>
                    <div className={styles.fieldEditorHeader}>
                        <h3>{editingAreaIndex !== null ? 'Editar Área' : 'Nova Área'}</h3>
                        <button
                            type="button"
                            className={styles.fieldEditorClose}
                            onClick={() => {
                                setAreaEditorOpen(false);
                                setEditingAreaIndex(null);
                            }}
                        >
                            ×
                        </button>
                    </div>

                    <div className={styles.fieldEditorBody}>
                        <div className={styles.fieldEditorGroup}>
                            <label>
                                Título da Área <span className={styles.required}>*</span>
                            </label>
                            <input
                                type="text"
                                value={areaForm.title}
                                onChange={(e) => setAreaForm({ ...areaForm, title: e.target.value })}
                                placeholder="Ex: Dados Gerais"
                            />
                        </div>
                        <div className={styles.fieldEditorGroup}>
                            <label>Descrição</label>
                            <textarea
                                value={areaForm.description}
                                onChange={(e) => setAreaForm({ ...areaForm, description: e.target.value })}
                                placeholder="Descreva o propósito desta área"
                                rows={3}
                            />
                        </div>
                    </div>

                    <div className={styles.fieldEditorFooter}>
                        <button
                            type="button"
                            className={styles.fieldEditorCancel}
                            onClick={() => {
                                setAreaEditorOpen(false);
                                setEditingAreaIndex(null);
                            }}
                        >
                            Cancelar
                        </button>
                        <button
                            type="button"
                            className={styles.fieldEditorSave}
                            onClick={saveArea}
                        >
                            {editingAreaIndex !== null ? 'Atualizar' : 'Adicionar'}
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    const renderAreas = () => {
        if (formData.areas.length === 0) {
            return (
                <div className={styles.emptyFields}>
                    <p>Nenhuma área adicionada</p>
                    <span>Clique em "Nova Área" para começar a estruturar seu checklist</span>
                </div>
            );
        }

        return (
            <div className={styles.areasContainer}>
                {formData.areas.map((area, areaIndex) => (
                    <div key={areaIndex} className={styles.areaCard}>
                        <div className={styles.areaHeader}>
                            <div className={styles.areaHeaderLeft}>
                                <div className={styles.areaTitle}>{area.title || 'Área sem título'}</div>
                                {area.description && (
                                    <div className={styles.areaDescription}>{area.description}</div>
                                )}
                            </div>
                            <div className={styles.areaActions}>
                                <button
                                    type="button"
                                    className={styles.areaActionBtn}
                                    onClick={() => addItem({ areaIndex, subAreaIndex: undefined })}
                                    title="Novo Item"
                                >
                                    + Item
                                </button>
                                <button
                                    type="button"
                                    className={styles.areaActionBtn}
                                    onClick={() => addSubArea(areaIndex)}
                                    title="Nova Subárea"
                                >
                                    + Subárea
                                </button>
                                <button
                                    type="button"
                                    className={styles.areaActionBtn}
                                    onClick={() => moveArea(areaIndex, -1)}
                                    disabled={areaIndex === 0}
                                >
                                    ↑
                                </button>
                                <button
                                    type="button"
                                    className={styles.areaActionBtn}
                                    onClick={() => moveArea(areaIndex, 1)}
                                    disabled={areaIndex === formData.areas.length - 1}
                                >
                                    ↓
                                </button>
                                <button
                                    type="button"
                                    className={styles.areaActionBtn}
                                    onClick={() => editArea(areaIndex)}
                                >
                                    ✎
                                </button>
                                <button
                                    type="button"
                                    className={`${styles.areaActionBtn} ${styles.deleteBtn}`}
                                    onClick={() => deleteArea(areaIndex)}
                                >
                                    ×
                                </button>
                            </div>
                        </div>

                        <div className={styles.areaContent}>
                            {/* Render items in main area */}
                            {area.items && area.items.length > 0 && (
                                <div className={styles.itemsList}>
                                    {area.items.map((item, itemIndex) => (
                                        <div key={itemIndex} className={styles.itemCard}>
                                            <div className={styles.itemInfo}>
                                                <span className={styles.itemType}>
                                                    {FIELD_TYPE_LABELS[item.type] || item.type}
                                                    {item.subType && ` (${SUB_TYPE_LABELS[item.subType] || item.subType})`}
                                                </span>
                                                <span className={styles.itemTitle}>{item.title || 'Item sem título'}</span>
                                                {item.required && <span className={styles.itemRequired}>*</span>}
                                            </div>
                                            <div className={styles.itemActions}>
                                                <button
                                                    type="button"
                                                    className={styles.itemActionBtn}
                                                    onClick={() => moveItem({ areaIndex, subAreaIndex: undefined }, itemIndex, -1)}
                                                    disabled={itemIndex === 0}
                                                >
                                                    ↑
                                                </button>
                                                <button
                                                    type="button"
                                                    className={styles.itemActionBtn}
                                                    onClick={() => moveItem({ areaIndex, subAreaIndex: undefined }, itemIndex, 1)}
                                                    disabled={itemIndex === area.items.length - 1}
                                                >
                                                    ↓
                                                </button>
                                                <button
                                                    type="button"
                                                    className={styles.itemActionBtn}
                                                    onClick={() => editItem({ areaIndex, subAreaIndex: undefined }, itemIndex)}
                                                >
                                                    ✎
                                                </button>
                                                <button
                                                    type="button"
                                                    className={`${styles.itemActionBtn} ${styles.deleteBtn}`}
                                                    onClick={() => deleteItem({ areaIndex, subAreaIndex: undefined }, itemIndex)}
                                                >
                                                    ×
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Render sub-areas */}
                            {area.subAreas && area.subAreas.length > 0 && (
                                <div className={styles.subAreasContainer}>
                                    {area.subAreas.map((subArea, subAreaIndex) => (
                                        <div key={subAreaIndex} className={styles.subAreaCard}>
                                            <div className={styles.subAreaHeader}>
                                                <div className={styles.subAreaTitle}>
                                                    {subArea.title || 'Subárea sem título'}
                                                    {subArea.description && (
                                                        <span className={styles.subAreaDescription}>
                                                            - {subArea.description}
                                                        </span>
                                                    )}
                                                </div>
                                                <div className={styles.subAreaActions}>
                                                    <button
                                                        type="button"
                                                        className={styles.subAreaActionBtn}
                                                        onClick={() => addItem({ areaIndex, subAreaIndex })}
                                                        title="Novo Item"
                                                    >
                                                        + Item
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className={styles.subAreaActionBtn}
                                                        onClick={() => moveSubArea(areaIndex, subAreaIndex, -1)}
                                                        disabled={subAreaIndex === 0}
                                                    >
                                                        ↑
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className={styles.subAreaActionBtn}
                                                        onClick={() => moveSubArea(areaIndex, subAreaIndex, 1)}
                                                        disabled={subAreaIndex === area.subAreas.length - 1}
                                                    >
                                                        ↓
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className={styles.subAreaActionBtn}
                                                        onClick={() => editSubArea(areaIndex, subAreaIndex)}
                                                    >
                                                        ✎
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className={`${styles.subAreaActionBtn} ${styles.deleteBtn}`}
                                                        onClick={() => deleteSubArea(areaIndex, subAreaIndex)}
                                                    >
                                                        ×
                                                    </button>
                                                </div>
                                            </div>

                                            {/* Items in sub-area */}
                                            {subArea.items && subArea.items.length > 0 && (
                                                <div className={styles.itemsList}>
                                                    {subArea.items.map((item, itemIndex) => (
                                                        <div key={itemIndex} className={styles.itemCard}>
                                                            <div className={styles.itemInfo}>
                                                                <span className={styles.itemType}>
                                                                    {FIELD_TYPE_LABELS[item.type] || item.type}
                                                                    {item.subType && ` (${SUB_TYPE_LABELS[item.subType] || item.subType})`}
                                                                </span>
                                                                <span className={styles.itemTitle}>{item.title || 'Item sem título'}</span>
                                                                {item.required && <span className={styles.itemRequired}>*</span>}
                                                            </div>
                                                            <div className={styles.itemActions}>
                                                                <button
                                                                    type="button"
                                                                    className={styles.itemActionBtn}
                                                                    onClick={() => moveItem({ areaIndex, subAreaIndex }, itemIndex, -1)}
                                                                    disabled={itemIndex === 0}
                                                                >
                                                                    ↑
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    className={styles.itemActionBtn}
                                                                    onClick={() => moveItem({ areaIndex, subAreaIndex }, itemIndex, 1)}
                                                                    disabled={itemIndex === subArea.items.length - 1}
                                                                >
                                                                    ↓
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    className={styles.itemActionBtn}
                                                                    onClick={() => editItem({ areaIndex, subAreaIndex }, itemIndex)}
                                                                >
                                                                    ✎
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    className={`${styles.itemActionBtn} ${styles.deleteBtn}`}
                                                                    onClick={() => deleteItem({ areaIndex, subAreaIndex }, itemIndex)}
                                                                >
                                                                    ×
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    const renderPreview = () => {
        if (!previewMode) return null;

        const renderItemPreview = (item, index) => {
            const getInputPreview = () => {
                const isGPS = item.type === FIELD_TYPES.SPECIAL &&
                    (item.subType === SUB_TYPES.SPECIAL.GPS_MANUAL || item.subType === SUB_TYPES.SPECIAL.GPS_AUTOMATIC);

                if (isGPS) {
                    return (
                        <div className={styles.previewGPS}>
                            <div className={styles.previewGPSRow}>
                                <span>Latitude: <input type="text" className={styles.previewInput} placeholder="-23.550520" /></span>
                            </div>
                            <div className={styles.previewGPSRow}>
                                <span>Longitude: <input type="text" className={styles.previewInput} placeholder="-46.633308" /></span>
                            </div>
                        </div>
                    );
                }

                switch (item.type) {
                    case FIELD_TYPES.AVALIATIVE:
                        return (
                            <div className={styles.previewAvaliative}>
                                <button className={styles.previewAvaliativeYes}>S</button>
                                <button className={styles.previewAvaliativeNo}>N</button>
                            </div>
                        );
                    case FIELD_TYPES.TEXT:
                        return item.subType === SUB_TYPES.TEXT.LONG ? (
                            <textarea className={styles.previewInput} rows={3} placeholder={item.title} />
                        ) : (
                            <input type="text" className={styles.previewInput} placeholder={item.title} />
                        );
                    case FIELD_TYPES.DATE:
                        return <input type="text" className={styles.previewInput} placeholder="Data" />;
                    case FIELD_TYPES.REGISTRATION:
                        return <input type="text" className={styles.previewInput} placeholder="Cadastro" />;
                    case FIELD_TYPES.NUMERIC:
                        return <input type="text" className={styles.previewInput} placeholder="Número" />;
                    case FIELD_TYPES.SELECTION:
                        return (
                            <select className={styles.previewSelect}>
                                <option value="">Selecione...</option>
                                {item.options.map((opt, i) => (
                                    <option key={i} value={opt.label}>{opt.label}</option>
                                ))}
                            </select>
                        );
                    default:
                        return <input type="text" className={styles.previewInput} placeholder={item.title} />;
                }
            };

            return (
                <div key={index} className={styles.previewField}>
                    <label className={styles.previewFieldLabel}>
                        {item.title}
                        {item.required && <span className={styles.previewFieldRequired}>*</span>}
                    </label>
                    {getInputPreview()}
                    {Object.entries(item.complements || {}).filter(([_, comp]) => comp.enabled).map(([key, comp]) => (
                        <div key={key} className={styles.previewComplement}>
                            <span className={styles.previewComplementLabel}>
                                {COMPLEMENT_LABELS[key]}
                                {comp.required && ' *'}
                            </span>
                            {key === COMPLEMENT_TYPES.COMMENTARY && (
                                <textarea className={styles.previewInput} rows={2} placeholder="Comentário..." />
                            )}
                            {key === COMPLEMENT_TYPES.ACTION_PLAN && (
                                <textarea className={styles.previewInput} rows={2} placeholder="Plano de ação..." />
                            )}
                            {key === COMPLEMENT_TYPES.ATTACHMENT && (
                                <input type="file" className={styles.previewInput} />
                            )}
                            {key === COMPLEMENT_TYPES.SIGNATURE && (
                                <div className={styles.previewSignature}>
                                    <input type="text" className={styles.previewInput} placeholder="Assinatura" />
                                </div>
                            )}
                            {key === COMPLEMENT_TYPES.NA && (
                                <div className={styles.previewNA}>
                                    <label className={styles.previewCheckbox}>
                                        <input type="checkbox" />
                                        Não se aplica
                                    </label>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            );
        };

        return (
            <div className={styles.previewContainer}>
                <div className={styles.previewHeader}>
                    <h3>Prévia do Checklist</h3>
                    <button
                        type="button"
                        className={styles.previewClose}
                        onClick={() => setPreviewMode(false)}
                    >
                        ✕ Fechar Prévia
                    </button>
                </div>
                <div className={styles.previewContent}>
                    <h2>{formData.name || 'Checklist sem nome'}</h2>
                    {formData.description && (
                        <p className={styles.previewDescription}>{formData.description}</p>
                    )}
                    <div className={styles.previewAreas}>
                        {formData.areas.map((area, areaIndex) => (
                            <div key={areaIndex} className={styles.previewArea}>
                                <h3 className={styles.previewAreaTitle}>{area.title || 'Área sem título'}</h3>
                                {area.description && (
                                    <p className={styles.previewAreaDescription}>{area.description}</p>
                                )}
                                {area.items && area.items.map((item, index) => renderItemPreview(item, index))}
                                {area.subAreas && area.subAreas.map((subArea, subIndex) => (
                                    <div key={subIndex} className={styles.previewSubArea}>
                                        <h4 className={styles.previewSubAreaTitle}>{subArea.title || 'Subárea sem título'}</h4>
                                        {subArea.description && (
                                            <p className={styles.previewSubAreaDescription}>{subArea.description}</p>
                                        )}
                                        {subArea.items && subArea.items.map((item, index) => renderItemPreview(item, index))}
                                    </div>
                                ))}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    };

    const renderChecklistList = () => {
        if (loading) {
            return (
                <div className={styles.skeletonTable}>
                    {[1, 2, 3, 4, 5].map(i => (
                        <div key={i} className={styles.skeletonRow}>
                            <div className={styles.skeletonCell} style={{ width: '30%' }}></div>
                            <div className={styles.skeletonCell} style={{ width: '20%' }}></div>
                            <div className={styles.skeletonCell} style={{ width: '15%' }}></div>
                            <div className={styles.skeletonCell} style={{ width: '20%' }}></div>
                            <div className={styles.skeletonCell} style={{ width: '15%' }}></div>
                        </div>
                    ))}
                </div>
            );
        }

        if (checklists.length === 0) {
            return (
                <div className={styles.emptyState}>
                    <div className={styles.emptyIcon}>📋</div>
                    <p>Nenhum checklist criado</p>
                    <span>Clique em "Novo Checklist" para começar</span>
                </div>
            );
        }

        return (
            <div className={styles.tableContainer}>
                <table className={styles.table}>
                    <thead>
                        <tr>
                            <th style={{ width: '25%' }}>Nome</th>
                            <th style={{ width: '20%' }}>Categoria</th>
                            <th style={{ width: '10%' }}>Áreas</th>
                            <th style={{ width: '15%' }}>Versão</th>
                            <th style={{ width: '15%' }}>Criado por</th>
                            <th style={{ width: '15%' }}>Ações</th>
                        </tr>
                    </thead>
                    <tbody>
                        {checklists.map(checklist => (
                            <tr key={checklist.id} className={styles.tableRow}>
                                <td>
                                    <strong>{checklist.name}</strong>
                                    {checklist.description && (
                                        <div className={styles.checklistDescription}>{checklist.description}</div>
                                    )}
                                </td>
                                <td>
                                    {checklist.category && (
                                        <span className={styles.categoryBadge}>{checklist.category}</span>
                                    )}
                                </td>
                                <td>{checklist.areaCount || checklist.areas?.length || 0}</td>
                                <td>
                                    <span className={styles.versionBadge}>v{checklist.version || '1.0'}</span>
                                </td>
                                <td>
                                    <span className={styles.userBadge}>
                                        {checklist.createdBy || 'Usuário'}
                                    </span>
                                </td>
                                <td>
                                    <div className={styles.actionsContainer}>
                                        {can.edit && (
                                            <button
                                                className={styles.actionButton}
                                                onClick={() => openModal(checklist)}
                                                title="Editar"
                                            >
                                                <FiEdit />
                                            </button>
                                        )}
                                        {can.export && (
                                            <button
                                                className={styles.actionButton}
                                                onClick={() => exportChecklist(checklist)}
                                                title="Exportar"
                                            >
                                                <RiFileTransferLine />
                                            </button>
                                        )}
                                        {can.duplicate && (
                                            <button
                                                className={styles.actionButton}
                                                onClick={() => duplicateChecklist(checklist)}
                                                title="Duplicar"
                                            >
                                                <HiOutlineDuplicate />
                                            </button>
                                        )}
                                        {can.delete && (
                                            <button
                                                className={`${styles.actionButton} ${styles.deleteButton}`}
                                                onClick={() => deleteChecklist(checklist)}
                                                title="Excluir"
                                            >
                                                <FiTrash2 />
                                            </button>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };

    // --------------------------------------------------------------
    // Main Render
    // --------------------------------------------------------------
    return (
        <div className={styles.pageContainer}>
            {/* Header */}
            <div className={styles.header}>
                <div>
                    <h1 className={styles.headerTitle}>📋 Modelos de Checklist TÜV</h1>
                </div>
                <div className={styles.headerActions}>
                    {can.create && (
                        <button
                            className={styles.addButton}
                            onClick={() => openModal()}
                        >
                            + Novo Checklist
                        </button>
                    )}
                </div>
            </div>

            {/* Status Messages */}
            {renderStatusBadge()}

            {/* Checklist List */}
            <div className={styles.reportsSection}>
                <div className={styles.reportsSectionHeader}>
                    <h2>Checklists Disponíveis</h2>
                    <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                        {checklists.length} {checklists.length === 1 ? 'checklist' : 'checklists'}
                    </span>
                </div>
                {renderChecklistList()}
            </div>

            {/* Modal for Create/Edit */}
            {isModalOpen && (
                <div className={styles.modalBackdrop} onClick={closeModal}>
                    <div
                        className={`${styles.modalContent} ${styles.checklistModal}`}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className={styles.modalHeader}>
                            <div>
                                <h2>{editingChecklist ? 'Editar Checklist' : 'Novo Checklist'}</h2>
                                {editingChecklist && (
                                    <p className={styles.modalSubtitle}>
                                        Editando: <strong>{editingChecklist.name}</strong> (v{editingChecklist.version || '1.0'})
                                    </p>
                                )}
                            </div>
                            <button
                                type="button"
                                className={styles.modalClose}
                                onClick={closeModal}
                            >
                                ×
                            </button>
                        </div>

                        <div className={styles.modalBody}>
                            <div className={styles.formContent}>
                                {/* Basic Info */}
                                <div className={styles.formSection}>
                                    <div className={styles.sectionHeader}>
                                        <div>
                                            <span className={styles.sectionIcon}>📝</span>
                                            <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                                                Informações Básicas
                                            </span>
                                        </div>
                                        <span className={styles.sectionBadge}>Obrigatório</span>
                                    </div>
                                    <div className={styles.formGrid}>
                                        <div className={styles.formGroup}>
                                            <label>
                                                Nome do Checklist <span className={styles.required}>*</span>
                                            </label>
                                            <input
                                                type="text"
                                                value={formData.name}
                                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                                placeholder="Ex: Inspeção de Qualidade"
                                                className={styles.formInput}
                                            />
                                        </div>
                                        <div className={styles.formGroup}>
                                            <label>Categoria</label>
                                            <input
                                                type="text"
                                                value={formData.category}
                                                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                                                placeholder="Ex: Qualidade, Segurança, Logística"
                                                className={styles.formInput}
                                            />
                                        </div>
                                    </div>

                                    <div className={styles.formGroup} style={{ padding: '0 20px 20px 20px' }}>
                                        <label>Descrição</label>
                                        <textarea
                                            value={formData.description}
                                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                            placeholder="Descreva o propósito deste checklist"
                                            className={styles.formTextarea}
                                            rows={2}
                                        />
                                    </div>

                                    <div className={styles.formRow}>
                                        <div className={styles.formGroup}>
                                            <label>Versão</label>
                                            <input
                                                type="text"
                                                value={formData.version}
                                                onChange={(e) => setFormData({ ...formData, version: e.target.value })}
                                                placeholder="1.0"
                                                className={styles.formInput}
                                                style={{ width: '120px' }}
                                            />
                                        </div>
                                        <div className={styles.formGroup}>
                                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={formData.isActive}
                                                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                                                />
                                                Checklist Ativo
                                            </label>
                                        </div>
                                    </div>
                                </div>

                                {/* Areas Management */}
                                <div className={styles.formSection}>
                                    <div className={styles.sectionHeader}>
                                        <div>
                                            <span className={styles.sectionIcon}>📋</span>
                                            <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                                                Áreas do Checklist
                                            </span>
                                        </div>
                                        <div className={styles.sectionActions}>
                                            <button
                                                type="button"
                                                className={styles.previewToggle}
                                                onClick={() => setPreviewMode(!previewMode)}
                                            >
                                                {previewMode ? '✕ Fechar Prévia' : '👁️ Prévia'}
                                            </button>
                                            <button
                                                type="button"
                                                className={styles.addFieldButton}
                                                onClick={addArea}
                                            >
                                                + Nova Área
                                            </button>
                                        </div>
                                    </div>

                                    {renderPreview()}
                                    <div style={{ padding: '0 20px 20px 20px' }}>
                                        {renderAreas()}
                                    </div>
                                    {renderAreaEditor()}
                                    {renderItemEditor()}
                                    {renderSubAreaEditor()}
                                </div>
                            </div>
                        </div>

                        <div className={styles.modalFooter}>
                            <button
                                type="button"
                                className={styles.cancelButton}
                                onClick={closeModal}
                            >
                                Cancelar
                            </button>
                            <button
                                type="button"
                                className={styles.saveButton}
                                onClick={saveChecklist}
                                disabled={loading}
                            >
                                {loading ? (
                                    <>
                                        <span className={styles.buttonSpinner}></span>
                                        Salvando...
                                    </>
                                ) : (
                                    'Salvar Checklist'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TUVChecklistModelPage;